import { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import { useApproveRequest, useRejectRequest } from '@/hooks/use-approvals'
import { getApiErrorMessage } from '@/lib/api'
import type { ApprovalRequest } from '@riverpark/types'

export function DecideApprovalModal({
  request,
  onClose,
}: {
  request: ApprovalRequest
  onClose: () => void
}) {
  const approve = useApproveRequest()
  const reject = useRejectRequest()
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [decision, setDecision] = useState<'approve' | 'reject' | null>(null)

  const pending = approve.isPending || reject.isPending

  async function handleApprove() {
    setError(null)
    setDecision('approve')
    try {
      await approve.mutateAsync({ id: request.id, note: note.trim() || undefined })
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not approve request'))
    }
  }

  async function handleReject() {
    setError(null)
    if (!note.trim()) {
      setError('A note explaining the rejection is required')
      return
    }
    setDecision('reject')
    try {
      await reject.mutateAsync({ id: request.id, note: note.trim() })
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not reject request'))
    }
  }

  return (
    <Modal open onClose={onClose} labelledBy="decide-approval-title">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-sans text-label uppercase tracking-[0.12em] text-muted-foreground">
              Requested by {request.initiatedByName}
            </p>
            <h2 id="decide-approval-title" className="font-serif text-xl text-ink">
              {request.summary}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>

      <ModalBody className="space-y-4">
        <p className="rounded border border-border-2 bg-cream-2 px-3 py-2 font-sans text-sm text-ink">
          <span className="font-medium">Reason given: </span>
          {request.reason}
        </p>
        <Input
          label="Note (required to reject, optional to approve)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. confirmed with resident, looks correct"
        />
        {error && (
          <p className="font-sans text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="border-destructive/40 text-destructive hover:border-destructive"
          onClick={() => void handleReject()}
          loading={pending && decision === 'reject'}
        >
          Reject
        </Button>
        <Button
          type="button"
          onClick={() => void handleApprove()}
          loading={pending && decision === 'approve'}
        >
          Approve
        </Button>
      </ModalFooter>
    </Modal>
  )
}
